import React, { useState, useEffect, useCallback } from "react";
import {
  listTaxConfigs,
  listTaxConfigsPortal,
  createTaxConfig,
  createTaxConfigPortal,
  deleteTaxConfig,
  deleteTaxConfigPortal
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Checkbox,
  List,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  Plus,
  Percent
} from "lucide-react";
import {
  useToast
} from "../../context/ToastContext";

const emptyForm = { name: "", code: "", ratePercent: "", isInclusive: false };

const TaxConfigPage = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await listTaxConfigsPortal() : await listTaxConfigs();
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load tax configurations.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, ratePercent: Number(form.ratePercent) };
      if (isPortal) await createTaxConfigPortal(payload);
      else await createTaxConfig(payload);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to create tax configuration.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this tax configuration?")) return;
    try {
      if (isPortal) await deleteTaxConfigPortal(id);
      else await deleteTaxConfig(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to delete tax configuration.");
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Tax configuration</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Define tax rates applied to fee items (e.g. GST, service tax).
        </p>
      </div>

      

      <Card>
        <CardHeader>
          <CardTitle>Add tax rate</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Display name"
              placeholder="e.g. GST 18%"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              label="Code"
              placeholder="e.g. GST18"
              required
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
            />
            <Input
              label="Rate (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="0.00"
              required
              value={form.ratePercent}
              onChange={(e) => setForm({ ...form, ratePercent: e.target.value })}
            />
            <div className="flex items-end pb-1">
              <Checkbox
                label="Inclusive of fee amount"
                checked={form.isInclusive}
                onChange={(checked) => setForm({ ...form, isInclusive: checked })}
              />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" isLoading={submitting} leftIcon={<Plus className="h-4 w-4" />}>
                Add tax rate
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-0 divide-y divide-neutral-100 dark:divide-neutral-800 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-card dark:shadow-black/20">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex flex-1 items-center gap-2">
                <Skeleton height={16} width="30%" />
                <Skeleton height={20} width={48} />
                <Skeleton height={14} width={40} />
              </div>
              <Skeleton height={28} width={64} />
            </div>
          ))}
        </div>
      ) : (
        <List
          items={rows.map((r) => ({
            id: r._id,
            content: (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.name}</span>
                <Badge variant="default">{r.code}</Badge>
                <span className="text-neutral-500 dark:text-neutral-400">{r.ratePercent}%</span>
                {r.isInclusive && <Badge variant="primary">Inclusive</Badge>}
              </div>
            ),
            trailing: (
              <Button
                variant="ghost"
                size="sm"
                className="text-danger-500 hover:text-danger-700"
                onClick={() => handleDelete(r._id)}
              >
                Remove
              </Button>
            ),
          }))}
          emptyState={
            <EmptyState
              icon={<Percent />}
              title="No tax rates configured"
              description="Add a tax rate to apply it to fee structures."
            />
          }
        />
      )}
    </div>
  );
};

export default TaxConfigPage;
